// @flow
import { useAuthContext } from 'auth/authContext'
import usePermissions from 'hooks/usePermissions'
import filter from 'lodash/filter'
import split from 'lodash/split'
import * as React from 'react'
import { useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Dropdown from 'ui/atoms/Dropdown'
import { openInNewTab } from 'utils/helpers'

import cn from '@/utils/tailwind/cn'

import zentakelogo from '../assets/zentakelogo.svg'
import Icons from '../ui/Icons'
import NavBarButton from './NavBar/NavBarButton'
import NavBarButtonMobile from './NavBar/NavBarButtonMobile'
import NavBarDropdown from './NavBar/NavBarDropdown'

export default function NavBar(): React.Node {
  const { pathname } = useLocation()
  const { canViewClientList, canViewGeneralSetting } = usePermissions()

  const { isAuthenticated, logout, user } = useAuthContext()

  const [isHamburgerActive, setHamburgerActive] = React.useState(false)
  const [hasNavBar, setHasNavBar] = React.useState(true)

  const willHideNavBar = React.useMemo(() => {
    try {
      const path = filter(split(pathname, '/'), (p) => p !== '')
      if (path[0] === 'pdf-filler' && ['preview', 'fill'].includes(path[2])) {
        return true
      }

      switch (path[0]) {
        case 'd':
        case 'r':
        case 'f':
        case 't':
        case 'k':
        case 's':
        case 'signup':
        case 'login':
        case 'forgot-password':
        case 'password':
        case 'verify-email':
        case 'setup-card': // hide navbar from setup-card page
        case 'closed-account':
          return true
        default:
          return false
      }
    } catch (error) {
      return false
    }
  }, [pathname])

  React.useEffect(() => {
    setHamburgerActive(false)
    setHasNavBar(!willHideNavBar)
  }, [pathname, willHideNavBar])

  const onHamburger = React.useCallback(() => {
    setHamburgerActive((isActive) => !isActive)
  }, [])

  const logoutWithRedirect = useCallback(
    () =>
      logout({
        returnTo: window.location.pathname,
      }),
    [logout]
  )

  const navItems = React.useMemo(() => {
    const items = []
    items.push({
      key: 'account',
      label: (
        <Link
          className="block px-4 py-2 text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 hover:text-zentake hover:no-underline focus:outline-none"
          to="/profile"
        >
          Account
        </Link>
      ),
    })
    if (canViewGeneralSetting) {
      items.push({
        key: 'settings',
        label: (
          <Link
            className="block border-transparent px-4 py-2 text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 hover:text-zentake hover:no-underline focus:border-transparent focus:outline-none focus:ring-0"
            to="/settings"
          >
            Settings
          </Link>
        ),
      })
    }
    items.push({
      key: 'feature-request',
      label: (
        <div
          className="block cursor-pointer px-4 py-2 text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 hover:text-zentake hover:no-underline focus:outline-none"
          onClick={(e) => {
            openInNewTab('https://zentake.featurebase.app/')
          }}
        >
          Feature Request
        </div>
      ),
    })
    items.push({
      key: 'sign-out',
      label: (
        <div
          className="block px-4 py-2 text-sm leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 hover:text-zentake focus:outline-none"
          onClick={() => {
            logoutWithRedirect()
          }}
        >
          Sign out
        </div>
      ),
    })
    return items
  }, [canViewGeneralSetting, logoutWithRedirect])

  return (
    <div>
      {isAuthenticated && hasNavBar && (
        <>
          <nav className="border-b bg-white">
            <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
              <div className="relative flex h-16 justify-between">
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                  <button
                    aria-expanded="false"
                    aria-label="Main menu"
                    className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 transition duration-150 ease-in-out hover:bg-gray-100 hover:text-gray-500 focus:bg-gray-100 focus:text-gray-500 focus:outline-none"
                    onClick={onHamburger}
                  >
                    <svg
                      className="block h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M4 6h16M4 12h16M4 18h16"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    <svg
                      className="hidden h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M6 18L18 6M6 6l12 12"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                  <Link className="flex flex-shrink-0 items-center" to="/">
                    <img
                      alt="Workflow logo"
                      className="block h-8 w-auto lg:hidden"
                      src={zentakelogo}
                    />
                    <img
                      alt="Workflow logo"
                      className="hidden h-8 w-auto lg:block"
                      src={zentakelogo}
                    />
                  </Link>
                  <div className="hidden sm:ml-6 sm:flex">
                    <NavBarButton path="/">Dashboard</NavBarButton>
                    <NavBarButton dataCy="form-list" path="/forms">
                      Forms
                    </NavBarButton>
                    <NavBarButton path="/packets">Packets</NavBarButton>
                    {canViewClientList && (
                      <NavBarButton path="/clients">Clients</NavBarButton>
                    )}
                    <NavBarDropdown>
                      <span>More</span>
                    </NavBarDropdown>
                  </div>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center space-x-3 pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                  <button
                    aria-label="Notifications"
                    className="rounded-full border-2 border-transparent p-1 text-gray-400 transition duration-150 ease-in-out hover:text-gray-500 focus:bg-gray-100 focus:text-gray-500 focus:outline-none"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>

                  <button
                    aria-label="Help"
                    className="rounded-full border-2 border-transparent p-1 text-gray-400 transition duration-150 ease-in-out hover:text-gray-500 focus:bg-gray-100 focus:text-gray-500 focus:outline-none"
                    onClick={() => openInNewTab('https://help.zentake.com')}
                  >
                    <Icons.Support size={6} />
                  </button>

                  <Dropdown items={navItems}>
                    <button
                      className={cn(
                        'flex items-center gap-1 rounded-full border-2 border-transparent bg-gray-100 text-sm transition duration-150 ease-in-out focus:border-gray-300 focus:outline-none',
                        user && 'pr-2'
                      )}
                    >
                      <Icons.UserCircle size={8} />
                      {user ? (
                        <p className="font-medium text-gray-500">
                          {user?.first_name} {user?.last_name}
                        </p>
                      ) : null}
                    </button>
                  </Dropdown>
                </div>
              </div>
            </div>
            <div className={`${isHamburgerActive ? 'block' : 'hidden'}`}>
              <div className="pb-1 pt-2">
                <NavBarButtonMobile path="/">Dashboard</NavBarButtonMobile>
                <NavBarButtonMobile path="/forms">Forms</NavBarButtonMobile>
                <NavBarButtonMobile path="/packets">Packets</NavBarButtonMobile>
                {canViewClientList && (
                  <NavBarButtonMobile path="/clients">
                    Clients
                  </NavBarButtonMobile>
                )}
                <NavBarButtonMobile path="/exports">Exports</NavBarButtonMobile>
              </div>
            </div>
          </nav>
        </>
      )}
    </div>
  )
}
